import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle, Plus, Image as ImageIcon } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

// Tooltip explanations for machine setup fields
const TOOLTIPS = {
  axis_count: "3-axis machines move X, Y, and Z. 4-axis adds rotation (A-axis) for cylindrical work.",
  motor_class: "NEMA17 are smaller and weaker. NEMA23/34 provide more torque for harder materials.",
  drive_type: "Belt drives are fast but less precise. Ball screws are most accurate. Lead screws are a middle ground.",
  motion_guides: "V-wheels are budget-friendly. Linear rails provide better rigidity and precision.",
  spindle_type: "Trim routers are common on hobby machines. Water-cooled spindles are quieter and more powerful.",
  controller_board: "Your controller firmware affects G-code compatibility and features.",
  rigidity: "Light machines flex under load. Heavy/rigid machines can run faster feeds with better finish.",
  collet_size: "The maximum tool shank diameter your spindle can hold. 1/4\" (6.35mm) is most common.",
};

// Helper component for field labels with tooltips and optional required indicator
const LabelWithTooltip = ({ children, tooltip, required }) => (
  <div className="flex items-center gap-1.5">
    <Label className="text-xs text-[#d6d6d6]">
      {children}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </Label>
    {tooltip && (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3.5 h-3.5 text-[#737373] hover:text-[#e67e22] cursor-help transition-colors" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-[#1f1f1f] border-[#3a3a3a] text-[#d6d6d6]">
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
  </div>
);

const MACHINE_CATEGORIES = [
  { id: 'overview', label: 'Full machine overview' },
  { id: 'motors', label: 'Stepper motors' },
  { id: 'rails', label: 'Linear rails / V-wheels' },
  { id: 'drive', label: 'Lead screws / ball screws / belts' },
  { id: 'spindle', label: 'Spindle or router' },
];

const REQUIRED_FOR_COMPLETE = [
  'axis_count',
  'motor_class',
  'spindle_type_manual',
  'controller_board',
  'rigidity',
];

const isSet = (v) => typeof v === 'string' && v.trim() !== '' && v.toLowerCase() !== 'unknown';

function MachineSetup() {
  const { user, token } = useContext(AuthContext);
  const [machines, setMachines] = useState([]);
  const [activeMachineId, setActiveMachineId] = useState('');
  const [showNewMachineForm, setShowNewMachineForm] = useState(false);
  const [newMachine, setNewMachine] = useState({
    name: '',
    bed_size_x: '',
    bed_size_y: '',
    bed_size_z: '',
    spindle_power: '',
    max_rpm: '',
    control_software: '',
    rigidity: 'medium',
  });
  const [profileForm, setProfileForm] = useState({
    axis_count: '',
    motor_class: '',
    drive_type_x: '',
    drive_type_y: '',
    drive_type_z: '',
    motion_guides: '',
    spindle_type_manual: '',
    controller_board: '',
    rigidity: '',
    collet_size: '',
  });
  const [savedProfileForm, setSavedProfileForm] = useState(null);
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('overview');
  const [machineImages, setMachineImages] = useState([]);

  useEffect(() => {
    const fetchMachines = async () => {
      try {
        // Fetch only router discipline machines
        const res = await axios.get(`${API}/machines?discipline=router`);
        setMachines(res.data || []);
        
        // Check localStorage for previously active machine
        const storedMachineId = localStorage.getItem('active_machine_router');
        const machineList = res.data || [];
        
        if (machineList.length > 0) {
          // If stored machine exists in list, use it; otherwise use first machine
          const activeId = storedMachineId && machineList.find(m => m.id === storedMachineId)
            ? storedMachineId
            : machineList[0].id;
          
          setActiveMachineId(activeId);
          localStorage.setItem('active_machine_router', activeId);
          hydrateProfileForm(machineList.find(m => m.id === activeId));
        }
      } catch (err) {
        console.error('Failed to load machines', err);
      }
    };
    fetchMachines();
  }, []);

  const hydrateProfileForm = (machine) => {
    const form = {
      axis_count: machine.axis_count || '',
      motor_class: machine.motor_class || '',
      drive_type_x: machine.drive_type_x || '',
      drive_type_y: machine.drive_type_y || '',
      drive_type_z: machine.drive_type_z || '',
      motion_guides: machine.motion_guides || '',
      spindle_type_manual: machine.spindle_type_manual || '',
      controller_board: machine.controller_board || '',
      rigidity: machine.rigidity || '',
      collet_size: machine.collet_size ? String(machine.collet_size) : '',
    };
    setProfileForm(form);
    setSavedProfileForm(form);
    setAnalysisSummary({
      profile_confidence_label: machine.profile_confidence_label || 'unknown',
      source: machine.profile_source || 'none',
    });
  };

  const handleMachineChange = (id) => {
    setActiveMachineId(id);
    // Persist active machine selection for router edition
    localStorage.setItem('active_machine_router', id);
    const m = machines.find((x) => x.id === id);
    if (m) hydrateProfileForm(m);
    fetchMachineImages(id);
  };

  const [machineImageUrl, setMachineImageUrl] = useState(null);
  const machineImageUrlRef = useRef(null);

  const fetchMachineImages = async (machineId) => {
    if (!machineId) {
      setMachineImages([]);
      if (machineImageUrlRef.current) {
        URL.revokeObjectURL(machineImageUrlRef.current);
        machineImageUrlRef.current = null;
      }
      setMachineImageUrl(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/machines/${machineId}/images`);
      const images = res.data || [];
      setMachineImages(images);
      
      // Clean up previous blob URL
      if (machineImageUrlRef.current && machineImageUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(machineImageUrlRef.current);
      }
      machineImageUrlRef.current = null;
      
      // Fetch the first image as a blob to avoid CORS issues
      if (images.length > 0 && images[0].url && token) {
        try {
          // Use native fetch for blob data
          const imageUrl = `${API}${images[0].url}?token=${encodeURIComponent(token)}`;
          const imageRes = await fetch(imageUrl);
          if (imageRes.ok) {
            const blob = await imageRes.blob();
            // Convert blob to data URL using Promise wrapper
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            machineImageUrlRef.current = dataUrl;
            setMachineImageUrl(dataUrl);
          } else {
            console.error('Failed to fetch machine image:', imageRes.status);
            setMachineImageUrl(null);
          }
        } catch (imgErr) {
          console.error('Failed to fetch machine image blob', imgErr);
          setMachineImageUrl(null);
        }
      } else {
        setMachineImageUrl(null);
      }
    } catch (err) {
      console.error('Failed to fetch machine images', err);
      setMachineImages([]);
      setMachineImageUrl(null);
    }
  };

  // Fetch images when active machine changes
  useEffect(() => {
    if (activeMachineId) {
      fetchMachineImages(activeMachineId);
    }
    // Cleanup blob URL on unmount
    return () => {
      if (machineImageUrlRef.current) {
        URL.revokeObjectURL(machineImageUrlRef.current);
        machineImageUrlRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMachineId, token]);

  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    if (!activeMachineId) return;
    try {
      const payload = Object.fromEntries(
        Object.entries(profileForm).filter(([_, v]) => v && String(v).trim() !== '')
      );
      // Convert collet_size to float for backend
      if (payload.collet_size) {
        payload.collet_size = parseFloat(payload.collet_size);
      }
      if (Object.keys(payload).length === 0) return;
      const res = await axios.put(`${API}/machines/${activeMachineId}`, payload);

      // Keep local machine list in sync so the "setup complete" banner updates immediately.
      setMachines((prev) =>
        (prev || []).map((m) => (m.id === activeMachineId ? { ...m, ...res.data } : m))
      );

      hydrateProfileForm(res.data);
    } catch (err) {
      console.error('Failed to update machine profile', err);
    }
  };

  // Unsaved changes detection
  const hasUnsavedChanges = savedProfileForm !== null &&
    JSON.stringify(profileForm) !== JSON.stringify(savedProfileForm);

  // Block browser tab close/refresh when dirty
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const createMachine = async () => {
    try {
      const payload = {
        ...newMachine,
        bed_size_x: parseFloat(newMachine.bed_size_x) || 0,
        bed_size_y: parseFloat(newMachine.bed_size_y) || 0,
        bed_size_z: parseFloat(newMachine.bed_size_z) || 0,
        spindle_power: parseFloat(newMachine.spindle_power) || 0,
        max_rpm: parseFloat(newMachine.max_rpm) || 0,
        discipline: 'router',  // Set discipline for router edition
      };
      const res = await axios.post(`${API}/machines`, payload);
      setMachines((prev) => [res.data, ...(prev || [])]);
      setActiveMachineId(res.data.id);
      // Persist new machine as active
      localStorage.setItem('active_machine_router', res.data.id);
      hydrateProfileForm(res.data);
      setShowNewMachineForm(false);
      setNewMachine({
        name: '',
        bed_size_x: '',
        bed_size_y: '',
        bed_size_z: '',
        spindle_power: '',
        max_rpm: '',
        control_software: '',
        rigidity: 'medium',
      });
    } catch (err) {
      console.error('Failed to create machine', err);
    }
  };

  const deleteActiveMachine = async () => {
    if (!activeMachineId) return;
    if (!window.confirm('Delete this machine? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/machines/${activeMachineId}`);
      setMachines((prev) => (prev || []).filter((m) => m.id !== activeMachineId));
      const remaining = machines.filter((m) => m.id !== activeMachineId);
      if (remaining.length) {
        setActiveMachineId(remaining[0].id);
        localStorage.setItem('active_machine_router', remaining[0].id);
        hydrateProfileForm(remaining[0]);
      } else {
        setActiveMachineId('');
        localStorage.removeItem('active_machine_router');
        hydrateProfileForm({});
      }
    } catch (err) {
      console.error('Failed to delete machine', err);
    }
  };

  const renameActiveMachine = async () => {
    if (!activeMachineId) return;
    const current = machines.find((m) => m.id === activeMachineId);
    const name = window.prompt('Rename machine:', current?.name || '');
    if (!name || !name.trim()) return;

    try {
      const res = await axios.put(`${API}/machines/${activeMachineId}`, { name: name.trim() });
      setMachines((prev) =>
        (prev || []).map((m) => (m.id === activeMachineId ? { ...m, name: res.data.name } : m))
      );
      hydrateProfileForm(res.data);
    } catch (err) {
      console.error('Failed to rename machine', err);
    }
  };

  const uploadChunk = async (machineId, imageId, chunk, index, totalChunks) => {
    // Send the blob directly - axios will handle binary transfer correctly
    await axios.post(`${API}/machines/${machineId}/images/${imageId}/chunk`, chunk, {
      params: { index, total_chunks: totalChunks },
      headers: {
        'Content-Type': 'application/octet-stream',
        'Authorization': `Bearer ${token}`,
      },
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!activeMachineId || files.length === 0) return;

    setUploading(true);
    try {
      const imageIds = [];
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      
      for (const file of files) {
        const initRes = await axios.post(`${API}/machines/${activeMachineId}/images/init`, {
          filename: file.name,
          content_type: file.type,
          category: selectedCategory,
        }, authHeaders);
        const { image_id: imageId, chunk_size: chunkSize } = initRes.data;
        imageIds.push(imageId);

        const totalChunks = Math.ceil(file.size / chunkSize);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          await uploadChunk(activeMachineId, imageId, chunk, i, totalChunks);
        }

        await axios.post(`${API}/machines/${activeMachineId}/images/${imageId}/complete`, {}, authHeaders);
      }

      await axios.post(`${API}/machines/${activeMachineId}/analyze-images`, { image_ids: imageIds }, authHeaders);

      // Refresh machine list to show updated inferred profile / confidence.
      const res = await axios.get(`${API}/machines`, authHeaders);
      setMachines(res.data || []);
      const updated = (res.data || []).find((m) => m.id === activeMachineId);
      if (updated) hydrateProfileForm(updated);
      
      // Refresh machine images to show newly uploaded images
      await fetchMachineImages(activeMachineId);
    } catch (err) {
      console.error('Failed image upload/analyze flow', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const activeMachine = useMemo(
    () => machines.find((m) => m.id === activeMachineId) || null,
    [machines, activeMachineId]
  );

  const missingFields = useMemo(() => {
    const missing = [];
    for (const f of REQUIRED_FOR_COMPLETE) {
      if (!isSet(profileForm[f] || '')) missing.push(f);
    }
    return missing;
  }, [profileForm]);

  const machineComplete = missingFields.length === 0;
  const anyMachineComplete = machines.some((m) =>
    REQUIRED_FOR_COMPLETE.every((f) => isSet(m?.[f] || ''))
  );

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#a3a3a3]">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.MY_EQUIPMENT)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Configure</div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Machine Setup
            </h1>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Answer a few questions so the calculator and troubleshooting can stay safe and conservative.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">

      {anyMachineComplete && (
        <div className="rounded-md border border-emerald-500/40 bg-black/60 backdrop-blur-sm px-4 py-3 text-xs text-emerald-100 flex justify-between items-center">
          <div>
            <div className="font-semibold text-emerald-300">Machine setup complete for at least one machine</div>
            <div className="text-emerald-200/80">
              You can add more machines or refine details any time. Your calculator and troubleshooting will use
              these profiles to stay on the safe side.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-emerald-500/60 text-emerald-100 hover:bg-emerald-900/40"
            onClick={() => (window.location.href = '/dashboard')}
          >
            Go to Dashboard
          </Button>
        </div>
      )}

      {showNewMachineForm && (
        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-base text-white">Add a machine</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="grid gap-2">
              <Label className="text-xs text-[#d6d6d6]">Machine name</Label>
              <Input
                value={newMachine.name}
                onChange={(e) => setNewMachine((p) => ({ ...p, name: e.target.value }))}
                className="bg-black/50 border-[#3a3a3a] text-white"
                placeholder="e.g. WorkBee 1000x1000"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="grid gap-2">
                <Label className="text-xs text-[#d6d6d6]">Bed size X (mm)</Label>
                <Input
                  value={newMachine.bed_size_x}
                  onChange={(e) => setNewMachine((p) => ({ ...p, bed_size_x: e.target.value }))}
                  className="bg-black/50 border-[#3a3a3a] text-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-[#d6d6d6]">Bed size Y (mm)</Label>
                <Input
                  value={newMachine.bed_size_y}
                  onChange={(e) => setNewMachine((p) => ({ ...p, bed_size_y: e.target.value }))}
                  className="bg-black/50 border-[#3a3a3a] text-white"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-[#d6d6d6]">Bed size Z (mm)</Label>
                <Input
                  value={newMachine.bed_size_z}
                  onChange={(e) => setNewMachine((p) => ({ ...p, bed_size_z: e.target.value }))}
                  className="bg-black/50 border-[#3a3a3a] text-white"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={createMachine} disabled={!newMachine.name.trim()}>
                Create machine
              </Button>
              <Button variant="outline" onClick={() => setShowNewMachineForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-base text-white">Your machines</CardTitle>
            <div className="text-xs text-[#a3a3a3] mt-1">
              Pick a machine to edit. Troubleshooting and calculator can use this profile.
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowNewMachineForm(true)} 
              className="border-[#e67e22] text-[#e67e22] hover:bg-[#e67e22] hover:text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add another machine
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={renameActiveMachine} disabled={!activeMachineId}>
                Rename
              </Button>
              <Button variant="destructive" size="sm" onClick={deleteActiveMachine} disabled={!activeMachineId}>
                Delete
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Machine selector and info */}
            <div className="flex-1 space-y-4">
              <div className="grid gap-2 max-w-md">
                <Label className="text-xs text-[#d6d6d6]">Select machine</Label>
                <Select value={activeMachineId} onValueChange={handleMachineChange}>
                  <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder={machines.length ? 'Select machine' : 'No machines yet'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    {machines.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-white">
                        {m.name || 'Machine'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {activeMachine && (
                <div className="text-xs text-[#a3a3a3]">
                  Source: <span className="text-white">{analysisSummary?.source || 'none'}</span> · Confidence:{' '}
                  <span className="text-white">{analysisSummary?.profile_confidence_label || 'unknown'}</span>
                </div>
              )}

              <div className="rounded-md border border-[#3a3a3a]/70 bg-black/50 px-4 py-3">
                <div className="text-xs font-semibold text-white">Setup checklist (this machine)</div>
                {machineComplete ? (
                  <div className="text-xs text-emerald-300 mt-1">Complete — this machine can be used for safe defaults.</div>
                ) : (
                  <div className="text-xs text-amber-300 mt-1">
                    Missing: {missingFields.join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-base text-white">Question-driven profile</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.axis_count} required>Machine axes</LabelWithTooltip>
              <Select value={profileForm.axis_count} onValueChange={(v) => handleProfileChange('axis_count', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="three_axis" className="text-white">3-axis machine</SelectItem>
                  <SelectItem value="four_axis" className="text-white">4-axis machine</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.motor_class} required>Motor Class</LabelWithTooltip>
              <Select value={profileForm.motor_class} onValueChange={(v) => handleProfileChange('motor_class', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="nema14" className="text-white">NEMA 14 (very small)</SelectItem>
                  <SelectItem value="nema17" className="text-white">NEMA 17 (common small)</SelectItem>
                  <SelectItem value="nema23" className="text-white">NEMA 23 (common medium)</SelectItem>
                  <SelectItem value="nema24" className="text-white">NEMA 24 (less common, larger frame)</SelectItem>
                  <SelectItem value="nema34" className="text-white">NEMA 34 (large)</SelectItem>
                  <SelectItem value="small_servos" className="text-white">Small servos</SelectItem>
                  <SelectItem value="industrial_servos" className="text-white">Industrial servos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.drive_type}>X axis drive type</LabelWithTooltip>
              <Select value={profileForm.drive_type_x} onValueChange={(v) => handleProfileChange('drive_type_x', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="belt" className="text-white">Belt</SelectItem>
                  <SelectItem value="lead_screw" className="text-white">Lead screw</SelectItem>
                  <SelectItem value="ball_screw" className="text-white">Ball screw</SelectItem>
                  <SelectItem value="rack_and_pinion" className="text-white">Rack & pinion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <Label className="text-xs text-[#d6d6d6]">Y axis drive type</Label>
              <Select value={profileForm.drive_type_y} onValueChange={(v) => handleProfileChange('drive_type_y', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="belt" className="text-white">Belt</SelectItem>
                  <SelectItem value="lead_screw" className="text-white">Lead screw</SelectItem>
                  <SelectItem value="ball_screw" className="text-white">Ball screw</SelectItem>
                  <SelectItem value="rack_and_pinion" className="text-white">Rack & pinion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <Label className="text-xs text-[#d6d6d6]">Z axis drive type</Label>
              <Select value={profileForm.drive_type_z} onValueChange={(v) => handleProfileChange('drive_type_z', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="lead_screw" className="text-white">Lead screw</SelectItem>
                  <SelectItem value="ball_screw" className="text-white">Ball screw</SelectItem>
                  <SelectItem value="belt" className="text-white">Belt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <Label className="text-xs text-[#d6d6d6]">Motion guides</Label>
              <Select value={profileForm.motion_guides} onValueChange={(v) => handleProfileChange('motion_guides', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="v_wheels" className="text-white">V-wheels</SelectItem>
                  <SelectItem value="linear_rails" className="text-white">Linear rails</SelectItem>
                  <SelectItem value="round_rails" className="text-white">Round rails</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.spindle_type} required>Spindle / router type</LabelWithTooltip>
              <Select
                value={profileForm.spindle_type_manual}
                onValueChange={(v) => handleProfileChange('spindle_type_manual', v)}
              >
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="trim_router" className="text-white">Trim router (Makita/DeWalt style)</SelectItem>
                  <SelectItem value="small_water_cooled" className="text-white">Small water-cooled spindle (0.81.5kW)</SelectItem>
                  <SelectItem value="medium_spindle" className="text-white">Medium spindle (2.2kW)</SelectItem>
                  <SelectItem value="high_power_spindle" className="text-white">High-power spindle (3kW+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3">
              <Label className="text-xs text-[#d6d6d6]">Collet size (max tool shank)</Label>
              <Select
                value={profileForm.collet_size}
                onValueChange={(v) => handleProfileChange('collet_size', v)}
              >
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="3.175" className="text-white">1/8&quot; (3.175mm)</SelectItem>
                  <SelectItem value="6.35" className="text-white">1/4&quot; (6.35mm) — Most common</SelectItem>
                  <SelectItem value="8" className="text-white">8mm</SelectItem>
                  <SelectItem value="10" className="text-white">10mm</SelectItem>
                  <SelectItem value="12.7" className="text-white">1/2&quot; (12.7mm)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#a3a3a3]">
                This helps filter compatible tools in the Bit Selector
              </p>
            </div>

            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.controller_board} required>Controller board</LabelWithTooltip>
              <Select
                value={profileForm.controller_board}
                onValueChange={(v) => handleProfileChange('controller_board', v)}
              >
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="grbl" className="text-white">GRBL (Arduino/ESP32 class)</SelectItem>
                  <SelectItem value="grblhal" className="text-white">grblHAL</SelectItem>
                  <SelectItem value="tinyg" className="text-white">TinyG</SelectItem>
                  <SelectItem value="smoothie" className="text-white">Smoothieware</SelectItem>
                  <SelectItem value="mach3" className="text-white">Mach3 (external motion controller)</SelectItem>
                  <SelectItem value="linuxcnc" className="text-white">LinuxCNC</SelectItem>
                  <SelectItem value="ucnc" className="text-white">UCCNC</SelectItem>
                  <SelectItem value="duet" className="text-white">Duet (RepRap)
                  </SelectItem>
                  <SelectItem value="blackbox" className="text-white">OpenBuilds BlackBox</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-3">
              <LabelWithTooltip tooltip={TOOLTIPS.rigidity} required>Rigidity</LabelWithTooltip>
              <Select value={profileForm.rigidity} onValueChange={(v) => handleProfileChange('rigidity', v)}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                  <SelectItem value="light" className="text-white">Light DIY / kit</SelectItem>
                  <SelectItem value="medium" className="text-white">Medium-duty hobbyist</SelectItem>
                  <SelectItem value="heavy" className="text-white">Heavier / more rigid machine</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 mt-2">
              <Button variant="default" onClick={saveProfile} disabled={!activeMachineId}>
                Save profile
              </Button>
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-400 font-medium">● Unsaved changes</span>
              )}
            </div>
            {activeMachineId && missingFields.length > 0 && (
              <p className="text-xs text-amber-400 mt-1">
                {missingFields.length} required field{missingFields.length !== 1 ? 's' : ''} still needed: {missingFields.join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-3 z-30">
        <div className="mx-auto max-w-6xl rounded-lg border border-slate-700/70 bg-slate-950/90 px-4 py-3 flex items-center justify-between gap-3 backdrop-blur">
          <div className="text-xs text-slate-200">
            {machineComplete ? (
              <span className="text-emerald-300">This machine is complete for troubleshooting + calculator defaults.</span>
            ) : (
              <span className="text-amber-300">This machine is incomplete — checklist above shows what’s missing.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => (window.location.href = '/troubleshooting')}>
              Troubleshoot this machine
            </Button>
            <Button size="sm" onClick={saveProfile} disabled={!activeMachineId}>
              Save
            </Button>
          </div>
        </div>
      </div>
      </div>
    </div>

  );
}

export default MachineSetup;
